import { useTranslation } from 'react-i18next';
import { Redirect, Route } from 'react-router-dom';
import { Flex } from 'reflexbox';

import { useFeature, useViewerRoles } from '@treams/treams-hooks';
import {
	CalendarIcon,
	CashIcon,
	ChevronLeftIcon,
	CompassIcon,
	ConversationIcon,
	DialogueIcon,
	DraftsIcon,
	FolderIcon,
	GearIcon,
	GiftIcon,
	SailboatIcon,
	SuitcaseIcon,
	TargetIcon,
} from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Avatar,
	Button,
	ContextMenu,
	Heading,
	Paragraph,
	Sidebar,
	Skeleton,
} from '@treams/treams-ui';

import JobPositionButton from './JobPositionButton';
import { useManagerMode } from './ManagerModeContext';

const UserHeader = Skeleton(
	function UserHeader(props: { user: graphql.User }) {
		const { t } = useTranslation();
		const { isWorkspaceSuperuser, isAdministrator, isHRBusinessPartner } = useViewerRoles();
		const jobPositions = props.user.jobPositions;
		const multiRoleConversion = useFeature(graphql.FeatureName.MultiRoleConversion);

		return (
			<Flex alignItems="center" flexDirection="column">
				<Avatar user={props.user} mb={2} mr={2} />

				<Heading mb={0}>
					{props.user.firstName} {props.user.lastName}
				</Heading>

				{multiRoleConversion ? (
					<>
						{jobPositions.length <= 2 ? (
							<>
								{jobPositions.map((jobPosition, index) => (
									<JobPositionButton
										userID={props.user.id}
										key={jobPosition.id}
										jobPosition={jobPosition}
										isLast={index === jobPositions.length - 1}
									/>
								))}
							</>
						) : (
							<ContextMenu.Target
								menu={
									<ContextMenu>
										{jobPositions.map((jobPosition) => (
											<JobPositionButton
												userID={props.user.id}
												key={jobPosition.id}
												jobPosition={jobPosition}
												isDropdown
											/>
										))}
									</ContextMenu>
								}
							>
								<Button
									title={t('sidebar.user.lookAtRoles', { length: jobPositions.length })}
									icon={<SuitcaseIcon />}
									mt={2}
									type="secondary"
									small
								/>
							</ContextMenu.Target>
						)}
					</>
				) : (
					<JobPositionButton userID={props.user.id} jobPosition={jobPositions[0]} />
				)}

				{props.user.mission.length > 0 && (
					<Paragraph mt={2} fontStyle="italic">
						{`"${props.user.mission}"`}
					</Paragraph>
				)}
				{(isWorkspaceSuperuser || isAdministrator) && (
					<Button
						link={`/settings/employees/${props.user.id}`}
						title={t('sidebar.user.edit')}
						icon={<GearIcon />}
						mt={2}
						type="secondary"
						small
					/>
				)}
				{isHRBusinessPartner && !isAdministrator && !isWorkspaceSuperuser && (
					<Button
						link={`/settings/employees/${props.user.id}`}
						title={t('common.module.newUI')}
						icon={<GearIcon />}
						mt={2}
						type="secondary"
						small
					/>
				)}
			</Flex>
		);
	},
	function UserHeaderSkeleton() {
		return (
			<Flex alignItems="center" flexDirection="column">
				<Avatar.Skeleton mb={2} />
				<Heading.Skeleton mb={2} />
				<Paragraph.Skeleton lines={1} />
			</Flex>
		);
	},
);

export default function UserNav(props: { userID: string }) {
	const { t } = useTranslation();

	const query = graphql.useUserQuery({ variables: { id: props.userID } });
	const salaryAccess = graphql.useUserSalaryAccessQuery({
		variables: { id: props.userID },
	});

	const hasGoals = useFeature(graphql.FeatureName.Goals);
	const hasConversations = useFeature(graphql.FeatureName.Evaluations);
	const hasFeedback = useFeature(graphql.FeatureName.Feedback);
	const hasCompliments = useFeature(graphql.FeatureName.Compliments);
	const hasNotes = useFeature(graphql.FeatureName.Notes);
	const hasGrowthPath = useFeature(graphql.FeatureName.GrowPath);
	const hasOneOnOnes = useFeature(graphql.FeatureName.OneOnOnes);
	const hasJobCompetenciesExplorer = useFeature(graphql.FeatureName.JobCompetenciesExplorer);
	const hasRewards = useFeature(graphql.FeatureName.Rewards);

	const managermode = useManagerMode();

	return (
		<>
			{managermode.enabled ? (
				<Button
					title={t('sidebar.manager.back')}
					type="secondary"
					icon={<ChevronLeftIcon />}
					link="/manager"
					mb={4}
				/>
			) : (
				<Button
					title={t('sidebar.user.back')}
					type="secondary"
					icon={<ChevronLeftIcon />}
					link="/"
					mb={4}
				/>
			)}
			{query.data && !query.loading ? (
				<UserHeader user={query.data.user as graphql.User} />
			) : (
				<UserHeader.Skeleton />
			)}
			<Sidebar.Spacing />
			{query.data && query.data.user.viewerCanViewJourney && (
				<>
					<Sidebar.Item
						icon={<SailboatIcon />}
						title={t('sidebar.peer.journey')}
						path={`/users/${props.userID}/journey`}
					/>
					<Route path="/users/:id" exact>
						{({ match }) => !!match && <Redirect to={`/users/${props.userID}/journey`} />}
					</Route>
				</>
			)}
			{query.data && query.data.user.viewerCanViewGoals && hasGoals && (
				<Sidebar.Item
					icon={<TargetIcon />}
					title={t('sidebar.peer.goals')}
					path={`/users/${props.userID}/goals`}
				/>
			)}
			{query.data && query.data.user.viewerCanViewFeedback && hasFeedback && (
				<Sidebar.Item
					icon={<DialogueIcon />}
					title={t('sidebar.peer.feedback')}
					path={`/users/${props.userID}/feedback`}
				/>
			)}
			{query.data && !query.data.user.viewerCanViewFeedback && hasCompliments && (
				<Sidebar.Item
					icon={<GiftIcon />}
					title={t('sidebar.peer.compliments')}
					path={`/users/${props.userID}/feedback/compliments`}
				/>
			)}
			{query.data && query.data.user.viewerCanViewFeedback && hasGrowthPath && (
				<Sidebar.Item
					icon={<CompassIcon />}
					title={
						hasJobCompetenciesExplorer
							? t('sidebar.peer.growthPath')
							: t('sidebar.peer.competencies')
					}
					path={`/users/${props.userID}/grow-path`}
				/>
			)}
			{query.data && query.data.user.viewerCanViewEvaluations && hasConversations && (
				<Sidebar.Item
					icon={<ConversationIcon />}
					title={t('sidebar.peer.conversations')}
					path={`/users/${props.userID}/conversations`}
				/>
			)}
			{hasNotes && (
				<Sidebar.Item
					icon={hasOneOnOnes ? <CalendarIcon /> : <DraftsIcon />}
					title={hasOneOnOnes ? t('sidebar.peer.oneOnOnes') : t('sidebar.peer.notes')}
					path={`/users/${props.userID}/notes`}
				/>
			)}
			{query.data && query.data.user.viewerCanViewDocuments && (
				<Sidebar.Item
					icon={<FolderIcon />}
					title={t('sidebar.peer.personalFiles')}
					path={`/users/${props.userID}/personal-files`}
				/>
			)}
			{hasRewards && salaryAccess.data?.user.viewerCanViewSalaryChanges && (
				<Sidebar.Item
					icon={<CashIcon />}
					title={t('sidebar.peer.salaryDevelopment')}
					path={`/users/${props.userID}/salary-development`}
				/>
			)}
		</>
	);
}
